import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { closeSurvey as closeSurveyService } from '../services/close-survey.service.js'
import { createSurvey } from '../services/create-survey.service.js'
import { deleteSurvey as deleteSurveyService } from '../services/delete-survey.service.js'
import { exportResponses as exportResponsesService } from '../services/export-responses.service.js'
import { getSurveyReport } from '../services/get-survey-report.service.js'
import { getSurvey as getSurveyService } from '../services/get-survey.service.js'
import { listSurveyTemplates } from '../services/list-survey-templates.service.js'
import { listSurveys } from '../services/list-surveys.service.js'
import { publishSurvey as publishSurveyService } from '../services/publish-survey.service.js'
import { sendInvitations as sendInvitationsService } from '../services/send-invitations.service.js'
import { updatePhysicalStructure as updatePhysicalStructureService } from '../services/update-physical-structure.service.js'
import { updateSurvey as updateSurveyService } from '../services/update-survey.service.js'

export const useGenericSurveyStore = defineStore('generic-survey', () => {
  const surveys = ref([])
  const total = ref(0)
  const isLoading = ref(false)
  const error = ref(null)
  const currentRequestId = ref(0)

  const templates = ref([])
  const isLoadingTemplates = ref(false)
  const templateError = ref(null)

  const currentSurvey = ref(null)
  const isLoadingCurrentSurvey = ref(false)

  const currentReport = ref(null)
  const isLoadingReport = ref(false)

  const isEmpty = computed(() => !isLoading.value && surveys.value.length === 0)

  async function fetchSurveys(parameters = {}) {
    const requestId = ++currentRequestId.value
    isLoading.value = true
    error.value = null
    try {
      const { items, total: totalCount } = await listSurveys(parameters)
      if (requestId !== currentRequestId.value)
        return
      surveys.value = items
      total.value = totalCount
    }
    catch (error_) {
      if (requestId !== currentRequestId.value)
        return
      error.value = error_?.message || 'Failed to load surveys'
    }
    finally {
      if (requestId === currentRequestId.value)
        isLoading.value = false
    }
  }

  async function fetchTemplates() {
    isLoadingTemplates.value = true
    templateError.value = null
    try {
      templates.value = await listSurveyTemplates()
    }
    catch (error_) {
      templateError.value = error_?.message || 'Failed to load templates'
    }
    finally {
      isLoadingTemplates.value = false
    }
  }

  async function fetchSurvey(id) {
    isLoadingCurrentSurvey.value = true
    currentSurvey.value = null
    try {
      currentSurvey.value = await getSurveyService(id)
    }
    catch (error_) {
      currentSurvey.value = null
      throw error_
    }
    finally {
      isLoadingCurrentSurvey.value = false
    }
  }

  async function fetchSurveyReport(id, filters = {}) {
    isLoadingReport.value = true
    currentReport.value = null
    try {
      currentReport.value = await getSurveyReport(id, filters)
    }
    catch (error_) {
      currentReport.value = null
      throw error_
    }
    finally {
      isLoadingReport.value = false
    }
  }

  async function closeSurvey(id) {
    const result = await closeSurveyService(id)
    if (currentSurvey.value?.id === id) {
      currentSurvey.value = { ...currentSurvey.value, status: result.status }
    }
    const index = surveys.value.findIndex(s => s.id === id)
    if (index !== -1) {
      surveys.value[index] = { ...surveys.value[index], status: result.status }
    }
    return result
  }

  async function deleteSurvey(id) {
    const result = await deleteSurveyService(id)
    if (currentSurvey.value?.id === id) {
      currentSurvey.value = null
    }
    surveys.value = surveys.value.filter(s => s.id !== id)
    total.value = Math.max(0, total.value - 1)
    return result
  }

  return {
    surveys,
    total,
    isLoading,
    error,
    isEmpty,
    fetchSurveys,
    templates,
    isLoadingTemplates,
    templateError,
    fetchTemplates,
    createSurvey,
    getSurvey: getSurveyService,
    updateSurvey: updateSurveyService,
    publishSurvey: publishSurveyService,
    sendInvitations: sendInvitationsService,
    currentSurvey,
    isLoadingCurrentSurvey,
    currentReport,
    isLoadingReport,
    fetchSurvey,
    fetchSurveyReport,
    closeSurvey,
    updatePhysicalStructure: updatePhysicalStructureService,
    deleteSurvey,
    exportResponses: exportResponsesService,
  }
})
